import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Btn, Field, Txt } from '../ui/primitives';
import { Icon } from '../ui/Icon';
import {
  PRESETS,
  type DateRange,
  type ResolvablePreset,
  type ResolveContext,
  daysInRange,
  matchPreset,
  rangeLabel,
  rangeSubLabel,
  resolvePreset,
  shiftRange,
} from '../utils/dateRange';

/**
 * Date-range control.
 *
 * A trigger button opens a popover holding the preset list, with a custom
 * from/to range behind a hairline in the footer. Selection is marked with a
 * check rather than colour alone. Presets apply immediately and close the
 * popover; a custom range needs an explicit Apply, because a half-typed range
 * would otherwise fire a query on every keystroke.
 *
 * Closes on outside click and on Escape, and restores focus to the trigger.
 */
export interface DateRangePickerProps {
  value: DateRange;
  ctx: ResolveContext;
  onChange: (range: DateRange) => void;
}

export function DateRangePicker({ value, ctx, onChange }: DateRangePickerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(value.from);
  const [draftTo, setDraftTo] = useState(value.to);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const activePreset = matchPreset(value, ctx);

  // Keep the custom inputs in step with whatever range is active.
  useEffect(() => {
    setDraftFrom(value.from);
    setDraftTo(value.to);
  }, [value.from, value.to]);

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  const applyPreset = (key: ResolvablePreset) => {
    onChange(resolvePreset(key, ctx));
    close();
  };

  const customInvalid = !draftFrom || !draftTo || draftFrom > draftTo;

  const applyCustom = () => {
    if (customInvalid) return;
    onChange({ from: draftFrom, to: draftTo });
    close();
  };

  const span = daysInRange(value);
  const stepLabel = span === 1 ? 'day' : `${span} days`;
  // Never step past today — there is no data in the future.
  const nextDisabled = shiftRange(value, 1).from > ctx.today;

  return (
    <Box className="relative flex items-center gap-1.5" ref={rootRef}>
      {/* Step the whole window back/forward by its own length. */}
      <Btn
        onPress={() => onChange(shiftRange(value, -1))}
        aria-label={`Previous ${stepLabel}`}
        title={`Previous ${stepLabel}`}
        className="grid place-items-center w-9 h-9 rounded-lg bg-surface-card text-ink-muted ring-1 ring-hairline hover:text-ink-primary hover:bg-surface-sunken transition-colors duration-150"
      >
        <Icon name="chevron-left" className="w-4 h-4" />
      </Btn>

      <Btn
        ref={triggerRef}
        onPress={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={`Change date range. Currently ${rangeSubLabel(value)}`}
        className={[
          'inline-flex items-center gap-2 h-9 pl-3 pr-2.5 rounded-lg text-[13px] font-medium transition-colors duration-150 ring-1',
          open
            ? 'bg-surface-sunken text-ink-primary ring-hairline'
            : 'bg-surface-card text-ink-primary ring-hairline hover:bg-surface-sunken',
        ].join(' ')}
      >
        <Box className="text-ink-muted">
          <Icon name="calendar" className="w-4 h-4" />
        </Box>
        <Box className="flex flex-col items-start leading-none">
          <Txt className="text-[13px] font-semibold text-ink-primary whitespace-nowrap">
            {rangeLabel(value, ctx)}
          </Txt>
          <Txt className="mt-0.5 text-[10px] text-ink-muted tabular whitespace-nowrap">{rangeSubLabel(value)}</Txt>
        </Box>
        <Box
          className="text-ink-muted transition-transform duration-150"
          style={{ transform: open ? 'rotate(180deg)' : 'none' }}
        >
          <Icon name="chevron-down" className="w-3.5 h-3.5" />
        </Box>
      </Btn>

      <Btn
        onPress={() => onChange(shiftRange(value, 1))}
        disabled={nextDisabled}
        aria-label={`Next ${stepLabel}`}
        title={nextDisabled ? 'Cannot go past today' : `Next ${stepLabel}`}
        className="grid place-items-center w-9 h-9 rounded-lg bg-surface-card text-ink-muted ring-1 ring-hairline hover:text-ink-primary hover:bg-surface-sunken transition-colors duration-150 disabled:opacity-30 disabled:pointer-events-none"
      >
        <Icon name="chevron-right" className="w-4 h-4" />
      </Btn>

      {open ? (
        <Box
          role="dialog"
          aria-label="Select date range"
          /**
           * Anchored left on narrow screens (where the header wraps and the
           * trigger sits at the left edge) and right on wider ones. Width is
           * clamped to the viewport so the popover can never overflow either
           * edge — a right-anchored 280px panel pushed off-screen on mobile.
           */
          className="absolute left-0 sm:left-auto sm:right-0 top-full mt-2 z-40 w-[min(280px,calc(100vw-2rem))] rounded-card bg-surface-card ring-1 ring-hairline shadow-lift overflow-hidden animate-fade-in"
        >
          <Box className="p-1.5">
            {PRESETS.map((preset) => {
              const active = activePreset === preset.key;
              const resolved = resolvePreset(preset.key, ctx);
              return (
                <Btn
                  key={preset.key}
                  onPress={() => applyPreset(preset.key)}
                  aria-pressed={active}
                  className={[
                    'w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-left transition-colors duration-150',
                    active ? 'bg-surface-sunken' : 'hover:bg-surface-sunken',
                  ].join(' ')}
                >
                  <Box className="min-w-0">
                    <Txt as="p" className="text-[13px] font-medium text-ink-primary">
                      {preset.label}
                    </Txt>
                    <Txt as="p" className="text-[11px] text-ink-muted tabular truncate">
                      {rangeSubLabel(resolved)}
                    </Txt>
                  </Box>
                  <Box
                    className="shrink-0 text-ink-primary"
                    style={{ opacity: active ? 1 : 0 }}
                    aria-hidden
                  >
                    <Icon name="check" className="w-4 h-4" strokeWidth={2.5} />
                  </Box>
                </Btn>
              );
            })}
          </Box>

          {/* Custom range, behind a hairline in the footer */}
          <Box className="border-t border-hairline p-3 bg-surface-raised">
            <Txt as="p" className="text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-muted">
              Custom range
            </Txt>
            <Box className="mt-2 flex items-center gap-2">
              <Field
                type="date"
                value={draftFrom}
                max={ctx.today}
                onChangeText={setDraftFrom}
                aria-label="Range start date"
                className="h-8 flex-1 min-w-0 px-2 rounded-lg bg-surface-card text-ink-primary text-[12px] tabular ring-1 ring-hairline [color-scheme:light] dark:[color-scheme:dark]"
              />
              <Txt className="text-[12px] text-ink-muted shrink-0">to</Txt>
              <Field
                type="date"
                value={draftTo}
                max={ctx.today}
                onChangeText={setDraftTo}
                aria-label="Range end date"
                className="h-8 flex-1 min-w-0 px-2 rounded-lg bg-surface-card text-ink-primary text-[12px] tabular ring-1 ring-hairline [color-scheme:light] dark:[color-scheme:dark]"
              />
            </Box>

            {customInvalid && draftFrom && draftTo ? (
              <Txt as="p" className="mt-1.5 text-[11px]" style={{ color: 'var(--life-returned)' }}>
                Start date must be on or before the end date.
              </Txt>
            ) : null}

            <Btn
              onPress={applyCustom}
              disabled={customInvalid}
              className="mt-2.5 w-full h-8 rounded-lg text-[12px] font-semibold bg-ink-primary text-surface-card hover:opacity-90 transition-opacity duration-150 disabled:opacity-40 disabled:pointer-events-none"
            >
              Apply range
            </Btn>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
